import {
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { branches } from "./branches";
import { customers } from "./customers";
import { orders } from "./orders";

export const walletAccounts = pgTable(
  "wallet_accounts",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    customerId: text("customer_id")
      .notNull()
      .references(() => customers.id, { onDelete: "cascade" }),
    status: text("status").$type<"ACTIVE" | "SUSPENDED" | "CLOSED">().notNull(),
    balanceLak: integer("balance_lak").notNull().default(0),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("wallet_accounts_customer_idx").on(t.customerId),
    index("wallet_accounts_status_idx").on(t.status),
  ],
);

export const walletLimits = pgTable(
  "wallet_limits",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    dailyTopupLimit: integer("daily_topup_limit"),
    dailySpendLimit: integer("daily_spend_limit"),
    maxBalance: integer("max_balance"),
    requireApprovalOverLak: integer("require_approval_over_lak"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [index("wallet_limits_account_idx").on(t.walletAccountId)],
);

export const walletTransactions = pgTable(
  "wallet_transactions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    type: text("type", {
      enum: ["TOPUP", "PAYMENT", "REFUND", "ADJUSTMENT"],
    }).notNull(),
    amountLak: integer("amount_lak").notNull(),
    status: text("status", {
      enum: ["PENDING", "APPROVED", "POSTED", "REJECTED"],
    })
      .$type<"PENDING" | "APPROVED" | "POSTED" | "REJECTED">()
      .notNull(),
    refOrderId: text("ref_order_id").references(() => orders.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("wallet_transactions_account_idx").on(t.walletAccountId),
    index("wallet_transactions_status_idx").on(t.status),
    index("wallet_transactions_created_idx").on(t.createdAt),
  ],
);

export const walletLedgerEntries = pgTable(
  "wallet_ledger_entries",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    txnId: text("txn_id")
      .notNull()
      .references(() => walletTransactions.id, { onDelete: "cascade" }),
    direction: text("direction", { enum: ["CREDIT", "DEBIT"] }).notNull(),
    amountLak: integer("amount_lak").notNull(),
    balanceAfterLak: integer("balance_after_lak").notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("wallet_ledger_entries_account_idx").on(t.walletAccountId),
    index("wallet_ledger_entries_txn_idx").on(t.txnId),
    index("wallet_ledger_entries_created_idx").on(t.createdAt),
  ],
);

export const topupRequests = pgTable(
  "topup_requests",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    method: text("method", { enum: ["CASHIER", "BANK_SLIP"] }).notNull(),
    amountLak: integer("amount_lak").notNull(),
    slipUrl: text("slip_url"),
    status: text("status")
      .$type<"PENDING" | "APPROVED" | "REJECTED">()
      .notNull(),
    requestedAt: timestamp("requested_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    reviewedBy: text("reviewed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", {
      withTimezone: true,
      mode: "date",
    }),
    /** QR counter top-up session id (unique when set) */
    sourceSessionId: text("source_session_id"),
    walletTransactionId: text("wallet_transaction_id").references(
      () => walletTransactions.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("topup_requests_account_idx").on(t.walletAccountId),
    index("topup_requests_status_idx").on(t.status),
    uniqueIndex("topup_requests_source_session_uidx").on(t.sourceSessionId),
    index("topup_requests_wallet_transaction_idx").on(t.walletTransactionId),
  ],
);

export const refundRequests = pgTable(
  "refund_requests",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => orders.id, { onDelete: "cascade" }),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    amountLak: integer("amount_lak").notNull(),
    reason: text("reason"),
    status: text("status")
      .$type<"PENDING" | "APPROVED" | "REJECTED">()
      .notNull(),
    requestedBy: text("requested_by"),
    reviewedBy: text("reviewed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", {
      withTimezone: true,
      mode: "date",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("refund_requests_order_idx").on(t.orderId),
    index("refund_requests_account_idx").on(t.walletAccountId),
  ],
);

export const qrTokens = pgTable(
  "qr_tokens",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull(),
    expiresAt: timestamp("expires_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    usedAt: timestamp("used_at", { withTimezone: true, mode: "date" }),
    usedByBranchId: text("used_by_branch_id").references(() => branches.id, {
      onDelete: "set null",
    }),
    purpose: text("purpose", { enum: ["PAYMENT", "TOPUP"] })
      .notNull()
      .default("PAYMENT"),
    /** ຍອດຫັກຈາກກະເປົາເມື່ອສະແກນ PAYMENT ສຳເລັດ (null ຈົນກວ່າ used_at) */
    chargedAmountLak: integer("charged_amount_lak"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("qr_tokens_account_idx").on(t.walletAccountId),
    index("qr_tokens_hash_idx").on(t.tokenHash),
    index("qr_tokens_expires_idx").on(t.expiresAt),
    index("qr_tokens_purpose_idx").on(t.purpose),
  ],
);

export const topupQrSessions = pgTable(
  "topup_qr_sessions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    walletAccountId: text("wallet_account_id")
      .notNull()
      .references(() => walletAccounts.id, { onDelete: "cascade" }),
    qrTokenId: text("qr_token_id")
      .notNull()
      .references(() => qrTokens.id, { onDelete: "cascade" }),
    displayCode: text("display_code").notNull(),
    status: text("status")
      .$type<"pending" | "completed" | "expired">()
      .notNull(),
    amountLak: integer("amount_lak"),
    expiresAt: timestamp("expires_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    completedAt: timestamp("completed_at", {
      withTimezone: true,
      mode: "date",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("topup_qr_sessions_account_idx").on(t.walletAccountId),
    uniqueIndex("topup_qr_sessions_qr_token_uidx").on(t.qrTokenId),
  ],
);
